'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import TestimonialCard from './TestimonialCard';
import FeaturedTestimonial from './FeaturedTestimonial';
import RatingSummary from './RatingSummary';
import VideoTestimonial from './VideoTestimonial';
import TestimonialFilters from './TestimonialFilters';

interface Testimonial {
  id: number;
  customerName: string;
  location: string;
  rating: number;
  date: string;
  reviewText: string;
  puppyVariety: string;
  customerImage: string;
  customerImageAlt: string;
  puppyImage?: string;
  puppyImageAlt?: string;
  highlights: string[];
}

interface FeaturedTestimonialData {
  id: number;
  customerName: string;
  location: string;
  rating: number;
  date: string;
  reviewText: string;
  extendedReview: string;
  puppyVariety: string;
  customerImage: string;
  customerImageAlt: string;
  puppyImages: Array<{
    url: string;
    alt: string;
  }>;
  highlights: string[];
}

interface VideoTestimonialData {
  id: number;
  customerName: string;
  location: string;
  thumbnailUrl: string;
  thumbnailAlt: string;
  videoUrl: string;
  duration: string;
  puppyVariety: string;
}

const TestimonialsInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedVariety, setSelectedVariety] = useState('all');
  const [selectedRating, setSelectedRating] = useState('all');
  const [selectedSort, setSelectedSort] = useState('recent');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const featuredTestimonial: FeaturedTestimonialData = {
    id: 1,
    customerName: "Sarah Mitchell",
    location: "Philadelphia, PA",
    rating: 5,
    date: "January 15, 2026",
    reviewText: "Our experience with Luminex Labradors was beyond amazing. From our first call to bringing our puppy home, everything was handled with such care and professionalism.",
    extendedReview: "We spent months researching different breeders before we discovered Luminex Labradors, and I'm so grateful we found them. Our yellow lab puppy, Max, came home healthy, beautifully socialized, and with all his paperwork perfectly organized. The team has been incredibly supportive, answering our questions even weeks after we brought Max home. He's brought so much joy and laughter to our family. The comprehensive health guarantee gave us complete peace of mind, and the transparent communication throughout the entire process made us feel confident we were making the right decision.",
    puppyVariety: "Yellow",
    customerImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1fc23a89f-1763301305375.png",
    customerImageAlt: "Professional woman with brown hair smiling warmly at camera in casual blue sweater",
    puppyImages: [
    {
      url: "https://images.unsplash.com/photo-1557579813-d85a86ebeba2",
      alt: "Adorable yellow Labrador puppy sitting on grass with bright eyes and happy expression"
    },
    {
      url: "/assets/images/Labrador_retriever_1_-1769800431629.jpg",
      alt: "White Labrador puppy with gentle expression and soft features"
    }],

    highlights: ["Super Healthy", "Amazing Personality", "Great Support", "Totally Transparent"]
  };

  const testimonials: Testimonial[] = [
  {
    id: 2,
    customerName: "Michael Chen",
    location: "New York, NY",
    rating: 5,
    date: "January 10, 2026",
    reviewText: "We got our chocolate lab puppy three months ago, and she's been absolutely perfect. You can really tell they care about health screening and early socialization. Our vet was super impressed with her condition and temperament. Highly recommend!",
    puppyVariety: "Chocolate",
    customerImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1ac5cc9db-1763294192404.png",
    customerImageAlt: "Asian man with glasses and friendly smile wearing gray shirt",
    puppyImage: "https://images.unsplash.com/photo-1536773802999-4ab3d027dc5f",
    puppyImageAlt: "Chocolate Labrador puppy with shiny brown coat lying on white blanket",
    highlights: ["Healthy Puppy", "Well Socialized", "Great Communication"]
  },
  {
    id: 3,
    customerName: "Jennifer Rodriguez",
    location: "Baltimore, MD",
    rating: 5,
    date: "December 28, 2025",
    reviewText: "Finding Luminex Labradors was the best thing we did. Our black lab puppy is smart, loving, and fits perfectly into our family. The health promise and ongoing support have been amazing. Thank you for bringing so much joy into our lives!",
    puppyVariety: "Black",
    customerImage: "https://img.rocket.new/generatedImages/rocket_gen_img_17c0179e8-1763295822377.png",
    customerImageAlt: "Hispanic woman with long dark hair smiling brightly in white top",
    puppyImage: "https://images.unsplash.com/photo-1614178730713-4badc749c333",
    puppyImageAlt: "Black Labrador puppy with glossy coat sitting attentively on wooden floor",
    highlights: ["Smart & Easy to Train", "Family Friendly", "Excellent Support"]
  },
  {
    id: 4,
    customerName: "David Thompson",
    location: "Wilmington, DE",
    rating: 5,
    date: "December 20, 2025",
    reviewText: "As first-time dog owners, we had tons of questions. The Luminex Labradors team patiently walked us through everything. Our English lab puppy is healthy, happy, and has the sweetest personality. We couldn't be happier!",
    puppyVariety: "English",
    customerImage: "https://img.rocket.new/generatedImages/rocket_gen_img_105550e4b-1763294981795.png",
    customerImageAlt: "Caucasian man with short brown hair and warm smile in navy blue shirt",
    puppyImage: "/assets/images/Labrador_retriever_3_-1769800537766.JPG",
    puppyImageAlt: "Yellow Labrador puppy with bright eyes and happy expression in outdoor setting",
    highlights: ["Perfect for First-Timers", "Patient Guidance", "Sweet Personality"]
  },
  {
    id: 5,
    customerName: "Emily Watson",
    location: "Boston, MA",
    rating: 5,
    date: "December 15, 2025",
    reviewText: "Our fox red lab puppy is absolutely gorgeous and has the most wonderful personality. Their commitment to quality and health shows in everything they do. From health checks to socialization, everything was top-notch. Highly recommended!",
    puppyVariety: "Fox Red",
    customerImage: "https://img.rocket.new/generatedImages/rocket_gen_img_18f14b269-1763296498382.png",
    customerImageAlt: "Young woman with blonde hair and bright smile in casual attire",
    puppyImage: "https://images.unsplash.com/photo-1677235329691-5d31bc033e11",
    puppyImageAlt: "Fox red Labrador puppy with distinctive reddish coat playing outdoors",
    highlights: ["Beautiful Puppy", "Top Quality", "Great Personality"]
  },
  {
    id: 6,
    customerName: "Robert Anderson",
    location: "Washington, DC",
    rating: 5,
    date: "December 5, 2025",
    reviewText: "We've had labs before, but our experience with LuminexLabradors set a new standard. The transparency, health guarantees, and post-adoption support are unmatched. Our yellow lab puppy is thriving and brings endless joy to our home.",
    puppyVariety: "Yellow",
    customerImage: "https://img.rocket.new/generatedImages/rocket_gen_img_191a43c60-1763294769851.png",
    customerImageAlt: "Middle-aged man with gray hair and professional appearance in business casual",
    puppyImage: "https://images.unsplash.com/photo-1540830815582-e3f2032c8f11",
    puppyImageAlt: "Yellow Labrador puppy with golden coat running happily through autumn leaves",
    highlights: ["Unmatched Support", "Transparent Process", "Thriving Puppy"]
  },
  {
    id: 7,
    customerName: "Lisa Martinez",
    location: "Richmond, VA",
    rating: 5,
    date: "November 28, 2025",
    reviewText: "Adopting our chocolate lab from LuminexLabradors was seamless. The breeder answered all our questions promptly and provided detailed care instructions. Our puppy arrived healthy and well-adjusted. We're so grateful for this wonderful addition to our family!",
    puppyVariety: "Chocolate",
    customerImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1f9281081-1763294529588.png",
    customerImageAlt: "Hispanic woman with curly hair and warm smile in colorful top",
    highlights: ["Seamless Process", "Prompt Communication", "Well-Adjusted Puppy"]
  },
  {
    id: 8,
    customerName: "James Wilson",
    location: "Newark, NJ",
    rating: 5,
    date: "November 20, 2025",
    reviewText: "Our black lab puppy has exceeded all our expectations. The health screening and genetic testing provided peace of mind. The breeder's expertise and dedication to the breed are evident. We highly recommend LuminexLabradors to anyone looking for a quality companion!",
    puppyVariety: "Black",
    customerImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1832b8ce4-1763292305805.png",
    customerImageAlt: "Professional man with beard and confident expression in business attire",
    highlights: ["Health Screening", "Genetic Testing", "Expert Breeder"]
  }];


  const videoTestimonials: VideoTestimonialData[] = [
  {
    id: 1,
    customerName: "Amanda Foster",
    location: "Princeton, NJ",
    thumbnailUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_1da9d8e50-1765541423457.png",
    thumbnailAlt: "Woman with brown hair smiling while holding yellow Labrador puppy in living room",
    videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
    duration: "2:45",
    puppyVariety: "Yellow"
  },
  {
    id: 2,
    customerName: "Kevin Brown",
    location: "Atlantic City, NJ",
    thumbnailUrl: "https://images.unsplash.com/photo-1484190929067-65e7edd5a22f",
    thumbnailAlt: "Man in casual shirt playing with chocolate Labrador puppy in backyard",
    videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
    duration: "3:12",
    puppyVariety: "Chocolate"
  }];


  const ratingSummary = {
    overallRating: 4.9,
    totalReviews: 247,
    ratingDistribution: {
      5: 235,
      4: 10,
      3: 2,
      2: 0,
      1: 0
    },
    categories: [
    { name: "Puppy Health", score: 5.0 },
    { name: "Communication", score: 4.9 },
    { name: "Puppy Quality", score: 4.9 },
    { name: "Support", score: 4.8 }]

  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-16">
          <div className="mb-12 text-center">
            <div className="mx-auto mb-4 h-12 w-48 animate-pulse rounded-md bg-muted" />
            <div className="mx-auto h-6 w-96 animate-pulse rounded-md bg-muted" />
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-16">
        {/* Hero Section */}
        <div className="mb-12 text-center">
          <h1 className="mb-4 font-heading text-[2.75rem] font-bold leading-[1.15] text-foreground md:text-[2rem]">
            Customer Testimonials
          </h1>
          <p className="mx-auto max-w-3xl text-lg text-muted-foreground">
            Read authentic experiences from our happy customers who have welcomed Luminex Labradors into their families. Their stories showcase our commitment to quality, health, and exceptional service.
          </p>
        </div>

        {/* Featured Testimonial */}
        <div className="mb-12">
          <FeaturedTestimonial testimonial={featuredTestimonial} />
        </div>

        {/* Rating Summary & Filters */}
        <div className="mb-12 grid gap-8 lg:grid-cols-3">
          <div className="lg:col-span-1">
            <RatingSummary
              overallRating={ratingSummary.overallRating}
              totalReviews={ratingSummary.totalReviews}
              ratingDistribution={ratingSummary.ratingDistribution}
              categories={ratingSummary.categories} />

          </div>
          <div className="lg:col-span-2">
            <TestimonialFilters
              selectedVariety={selectedVariety}
              selectedRating={selectedRating}
              selectedSort={selectedSort}
              onVarietyChange={setSelectedVariety}
              onRatingChange={setSelectedRating}
              onSortChange={setSelectedSort} />

          </div>
        </div>

        {/* Video Testimonials */}
        <div className="mb-12">
          <h2 className="mb-6 font-heading text-[2.25rem] font-semibold leading-[1.2] text-foreground md:text-[1.75rem]">
            Video Testimonials
          </h2>
          <div className="grid gap-8 md:grid-cols-2">
            {videoTestimonials.map((video) =>
            <VideoTestimonial key={video.id} video={video} />
            )}
          </div>
        </div>

        {/* All Testimonials */}
        <div className="mb-12">
          <h2 className="mb-6 font-heading text-[2.25rem] font-semibold leading-[1.2] text-foreground md:text-[1.75rem]">
            All Customer Reviews
          </h2>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {testimonials.map((testimonial) =>
            <TestimonialCard key={testimonial.id} testimonial={testimonial} />
            )}
          </div>
        </div>

        {/* CTA Section */}
        <div className="rounded-xl bg-gradient-to-br from-primary to-secondary p-12 text-center shadow-warm-lg">
          <h2 className="mb-4 font-heading text-[2.25rem] font-semibold leading-[1.2] text-primary-foreground md:text-[1.75rem]">
            Ready to Start Your Puppy Journey?
          </h2>
          <p className="mb-8 text-lg text-primary-foreground/90">
            Join hundreds of happy families who have found their perfect Labrador companion through us.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              href="/available-puppies"
              className="flex h-12 items-center gap-2 rounded-md bg-accent px-7 font-medium text-accent-foreground shadow-warm transition-smooth hover:scale-[0.97] hover:shadow-warm-md focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3">

              View Available Puppies
              <Icon name="ArrowRightIcon" size={20} />
            </Link>
            <Link
              href="/contact-us"
              className="flex h-12 items-center gap-2 rounded-md bg-card px-7 font-medium text-foreground shadow-warm transition-smooth hover:scale-[0.97] hover:shadow-warm-md focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3">

              Contact Us
              <Icon name="ChatBubbleLeftRightIcon" size={20} />
            </Link>
          </div>
        </div>
      </div>
    </div>);

};

export default TestimonialsInteractive;